import { GameId } from '@bg-shared/enums/GameId';
import currency from 'currency.js';
import { orderBy } from 'lodash-unified';
import { t } from '@bg-shared/business/Translate';
import { formatToCurrency } from '@betgames/bg-tools';
import { balanceSelectors, balanceStore } from '@bg-shared/business/Balance';
import { gamesMessagesEntity } from '@bg-shared/business/GamesMessages';
import { NOTIFICATION_ERROR_SHOW_TIME } from '@bg-shared/constants/constants';
import { GameMessageType } from '@bg-shared/business/GamesMessages/enums';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { authStore } from '@bg-shared/business/Auth';
import { gamesOptionsEntity } from '@bg-shared/business/GamesOptions';
import {
    webAPIIntegration,
    WebIntegrationErrorCodes,
    WebIntegrationType,
    IBGIWebIntegration,
} from '@bg-shared/integrations';
import { casinoBettingSelectors } from '../casinoBetting.selectors';
import {
    ICasinoRecentActionsModel,
    ICasinoValidationError,
    ICasinoValidationParams,
    ICasinoValidator,
} from '../interfaces';
import { optionIDConverter } from '../utils/optionIDConverter';
import { casinoBetAmountsEntity, casinoLimitsEntity, casinoValidationEntity } from '../store';

export class CasinoValidationService {
    protected readonly TRANSLATION_KEYS = {
        balance: '703',
        limits: 'range_bet_exceed_limits',
        maxAmount: 'maximum_bet',
        minAmount: 'minimum_bet',
    };

    protected readonly defaultValidationError: ICasinoValidationError = {
        isBlocking: true,
    };

    protected readonly betAmountsStore: ReturnType<typeof casinoBetAmountsEntity.store>;

    protected readonly limitsStore: ReturnType<typeof casinoLimitsEntity.store>;

    private readonly validationStore: ReturnType<typeof casinoValidationEntity.store>;

    protected gameOptionsStore: ReturnType<typeof gamesOptionsEntity.store>;

    private currentGameBalance: () => number;

    private timeout: number;

    protected defaultAddValidators: ICasinoValidator[] = [
        this.validateAuth,
        this.validateBalance,
        this.validateLimits,
    ];

    protected defaultMoveValidators: ICasinoValidator[] = [this.validateAuth, this.validateLimits];

    constructor(gameId: GameId) {
        this.betAmountsStore = casinoBetAmountsEntity.store(gameId);
        this.validationStore = casinoValidationEntity.store(gameId);
        this.limitsStore = casinoLimitsEntity.store(gameId);
        this.gameOptionsStore = gamesOptionsEntity.store(gameId);
        this.currentGameBalance = () => balanceSelectors.getValue(gameId)(balanceStore.value);
    }

    private setErrorCleanupTimeout(): void {
        this.timeout = setTimeout(() => {
            this.validationStore.reset();
        }, NOTIFICATION_ERROR_SHOW_TIME);
    }

    public resetErrorTimeout(): void {
        clearTimeout(this.timeout);
        this.validationStore.reset();
    }

    protected showError(message: string): void {
        gamesMessagesEntity.showMessage({
            message,
            timeout: NOTIFICATION_ERROR_SHOW_TIME,
            type: GameMessageType.INFO,
        });
    }

    public isBlockedByValidation(
        validation: ICasinoValidationError | [string, ICasinoValidationError][],
    ): boolean {
        if (!validation) {
            return false;
        }

        if (Array.isArray(validation)) {
            return validation.every(([, error]) => error?.isBlocking);
        }

        return validation?.isBlocking;
    }

    private validateAuth(): ICasinoValidationError {
        if (!authStore.value) {
            return { ...this.defaultValidationError, message: t.string('please_login') };
        }

        return null;
    }

    private validateBalance(params: ICasinoValidationParams): ICasinoValidationError {
        const totalBetAmount = casinoBettingSelectors.getTotalBetAmount(this.betAmountsStore.value);
        const totalStagedAmount = balanceSelectors.getTotalStagedAmount(balanceStore.value);

        const isValid =
            currency(totalBetAmount).add(params.additionalAmount).value <=
            currency(this.currentGameBalance()).value + totalStagedAmount;

        if (!isValid) {
            webAPIIntegration<IBGIWebIntegration>(WebIntegrationType.BGI, (api) => {
                api.errorMessage(
                    t.error(this.TRANSLATION_KEYS.balance),
                    WebIntegrationErrorCodes.BGI_BALANCE,
                );
            });

            return {
                ...this.defaultValidationError,
                message: t.error(this.TRANSLATION_KEYS.balance),
            };
        }

        return null;
    }

    protected validateLimits(params: ICasinoValidationParams): ICasinoValidationError {
        const limitsByOptionId = this.limitsStore.value?.validLimitsByOptionId;

        if (!limitsByOptionId) {
            return null;
        }

        const { oddId } = optionIDConverter.parse(params.optionItemID);

        const currentValue = this.betAmountsStore.value[params.optionItemID] || 0;

        const nextAmount = currency(currentValue).add(params.additionalAmount).value;

        const max = this.limitsStore.value.validLimitsByOptionId[oddId]?.max || null;
        const maxIsValid = !max || nextAmount <= max;

        const min = this.limitsStore.value.validLimitsByOptionId[oddId]?.min || null;
        const minIsValid = !min || nextAmount >= min;

        if (maxIsValid && minIsValid) {
            return null;
        }

        let diffAmount = 0;
        if (!maxIsValid && currentValue < max) {
            diffAmount = currency(max).subtract(currentValue).value;
        }

        const translationKey = !maxIsValid
            ? this.TRANSLATION_KEYS.maxAmount
            : this.TRANSLATION_KEYS.minAmount;

        const value = !maxIsValid ? max : min;

        return {
            isBlocking: !minIsValid || (!maxIsValid && !diffAmount),
            diffAmount,
            message: `${t.string(translationKey)} ${formatToCurrency(
                value,
                partnerSettingsStore.currency.precision,
                partnerSettingsStore.currency.template,
                partnerSettingsStore.currency.code,
                true,
            )}`,
        };
    }

    private iterateValidators(
        validators: ICasinoValidator[],
        options: ICasinoValidationParams,
    ): ICasinoValidationError[] {
        return validators.map((validator) => validator.call(this, options)).filter(Boolean);
    }

    public getMoveValidationErrors(options: ICasinoValidationParams): ICasinoValidationError {
        this.resetErrorTimeout();

        const allErrors = this.iterateValidators(this.defaultMoveValidators, options);
        const error = allErrors.length ? allErrors[0] : null;

        if (error) {
            this.validationStore.setErrors({ [options.optionItemID]: error });
            this.setErrorCleanupTimeout();
        }
        return error;
    }

    public getAddValidationErrors(
        optionsArray: ICasinoValidationParams[],
    ): Record<string, ICasinoValidationError> {
        const errors = optionsArray.reduce<Record<string, ICasinoValidationError>>(
            (prev, options) => {
                this.resetErrorTimeout();

                let allErrors = this.iterateValidators(this.defaultAddValidators, options);

                // Sort to have isBlocking errors at the top for a proper error tooltip message
                allErrors = orderBy(allErrors, ['isBlocking'], ['desc']);

                if (allErrors.length) {
                    prev[options.optionItemID] = { ...allErrors[0] };
                }

                return prev;
            },
            {},
        );

        if (Object.keys(errors).length) {
            this.validationStore.setErrors(errors);
            this.setErrorCleanupTimeout();
        }

        return errors;
    }

    public getDoubleValidationErrors():
        | [string, ICasinoValidationError][]
        | ICasinoValidationError {
        this.resetErrorTimeout();

        const balanceError = this.validateBalance({
            additionalAmount: casinoBettingSelectors.getTotalBetAmount(this.betAmountsStore.value),
        });

        if (balanceError) {
            this.showError(balanceError.message);
            return this.defaultValidationError;
        }

        const limitsErrors = Object.entries(this.betAmountsStore.value).map<
            [string, ICasinoValidationError]
        >(([optionItemID, amount]) => [
            optionItemID,
            this.validateLimits({ optionItemID, additionalAmount: amount }),
        ]);

        const errorWithHighlight = limitsErrors.filter(
            ([, error]) => error?.isBlocking || !!error?.diffAmount,
        );

        if (errorWithHighlight.length) {
            this.validationStore.setHighlight(errorWithHighlight.map(([id]) => id));
            this.setErrorCleanupTimeout();
            this.showError(t.error(this.TRANSLATION_KEYS.limits));
        }

        return limitsErrors;
    }

    public validateRepeat(bets: ICasinoRecentActionsModel['recentBets']): boolean {
        this.resetErrorTimeout();

        const balanceValidationError = this.validateBalance({
            additionalAmount: bets
                .map((bet) => Number(bet.amount))
                .reduce((result, amount) => currency(result).add(amount).value, 0),
        });

        if (balanceValidationError) {
            this.showError(balanceValidationError.message);
            return false;
        }

        const limitsValidation = bets.some((bet) =>
            this.isBlockedByValidation(
                this.validateLimits({
                    optionItemID: optionIDConverter.create(bet.oddId, bet.items),
                    additionalAmount: Number(bet.amount),
                }),
            ),
        );

        if (limitsValidation) {
            this.showError(t.error(this.TRANSLATION_KEYS.limits));
            return false;
        }

        return true;
    }

    public removeInvalidAmounts(): void {
        const invalidKeys = Object.keys(this.betAmountsStore.value).filter((key) => {
            const { oddId, items } = optionIDConverter.parse(key);
            const itemsCount = items?.filter((item) => item)?.length || 0;
            const validItemsCount =
                itemsCount === this.gameOptionsStore.value.options[oddId].itemsCount;
            const validAmount = this.betAmountsStore.value[key] > 0;

            return !validItemsCount || !validAmount;
        });

        this.betAmountsStore.clearAmounts(invalidKeys);
    }
}
